// SAM-1096 — what residents see in the app, drawn at the app's own sizes inside a phone frame.
// Sizes follow the canvas mocks (C3–C6): Featured card 358×160 with a 140×160 image, article hero 390×250.
import React from 'react';
import { S3Image } from '../S3Image';
import { renderMarkdownLite } from '../../utils/markdownLite';

const FONT = "'Red Hat Text','Segoe UI',system-ui,sans-serif";
const clamp = (n: number): React.CSSProperties => ({
  display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden',
});

/** 340 px phone; the 390 px app screen inside is scaled to fit. */
export const PhoneFrame: React.FC<{ height?: number; label?: string; children: React.ReactNode }> = ({ height = 700, label, children }) => (
  <div
    role="img"
    aria-label={label ?? 'Phone preview'}
    className="relative shrink-0 overflow-hidden bg-white"
    style={{ width: 340, height, boxSizing: 'border-box', border: '7px solid #1C1C1E', borderRadius: 42 }}
  >
    <div style={{ width: 390, zoom: 0.8359, fontFamily: FONT, color: '#000', background: '#fff', minHeight: height / 0.8359 }}>{children}</div>
  </div>
);

const Img: React.FC<{ imageKey?: string | null; width: number | string; height: number; radius?: number }> = ({ imageKey, width, height, radius }) => (
  <div style={{ width, height, borderRadius: radius, overflow: 'hidden', background: '#E5E7EB', flexShrink: 0 }}>
    {imageKey ? (
      <S3Image imageKey={imageKey} alt="" showLoading={false} fallbackComponent={<span />} style={{ width: '100%', height, objectFit: 'cover', display: 'block' }} />
    ) : null}
  </div>
);

export interface AdCardData {
  title: string;
  text: string;
  color?: string;
  imageKey?: string | null;
}

/** The Featured card: 2 lines of title, 2 lines of text, "Read more", image on the right. */
export const AdCard: React.FC<{ ad: AdCardData }> = ({ ad }) => (
  <div style={{ width: 358, height: 160, display: 'flex', borderRadius: 12, overflow: 'hidden', background: ad.color || '#F6E4CC', flexShrink: 0 }}>
    <div style={{ flex: 1, minWidth: 0, padding: '10px 0 10px 12px', display: 'flex', flexDirection: 'column', gap: 8, boxSizing: 'border-box' }}>
      <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2, color: '#000', ...clamp(2) }}>{ad.title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.3, color: '#000', ...clamp(2) }}>{ad.text}</div>
      <div style={{ flex: 1 }} />
      <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 4, background: '#fff', borderRadius: 30, padding: '8px 12px', fontSize: 14, color: '#000' }}>
        Read more →
      </div>
    </div>
    <Img imageKey={ad.imageKey} width={140} height={160} />
  </div>
);

const Dots: React.FC<{ count: number; active: number }> = ({ count, active }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 8 }}>
    {Array.from({ length: Math.max(1, count) }, (_, i) => (
      <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i === active ? '#000' : '#D9D9D9' }} />
    ))}
  </div>
);

export interface HeroBackground {
  imageKey?: string | null;
  fit?: 'cover' | 'contain';
  focalX?: number;
  focalY?: number;
}

/** Top of the Home: the tenant photo (with its fit and focal point) or the default gradient. */
const Hero: React.FC<{ bg?: HeroBackground }> = ({ bg }) => (
  <div style={{ height: 150, position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg,#BFE0DC 0%,#E6F2F0 70%,#fff 100%)' }}>
    {bg?.imageKey ? <HeroImage {...bg} imageKey={bg.imageKey} /> : null}
  </div>
);

const HeroImage: React.FC<HeroBackground & { imageKey: string }> = ({ imageKey, fit = 'cover', focalX = 50, focalY = 50 }) => (
  <S3Image
    imageKey={imageKey}
    alt=""
    showLoading={false}
    fallbackComponent={<span />}
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: fit, objectPosition: `${focalX}% ${focalY}%` }}
  />
);

const SectionHead: React.FC<{ title: string; more: string }> = ({ title, more }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
    <span style={{ fontSize: 24, fontWeight: 700 }}>{title}</span>
    <span style={{ fontSize: 18 }}>{more}</span>
  </div>
);

/** Home: the Featured carousel (first card shown, dots for the rest). Hidden when switched off or empty. */
export const FeaturedPreview: React.FC<{ ads: AdCardData[]; active?: number; title?: string; readAll?: string; bg?: HeroBackground; enabled?: boolean }> = ({
  ads, active = 0, title = 'Featured', readAll = 'Read all', bg, enabled = true,
}) => (
  <>
    <Hero bg={bg} />
    {enabled && ads.length > 0 && (
      <div style={{ padding: '0 16px', marginTop: 16 }}>
        <SectionHead title={title} more={readAll} />
        <AdCard ad={ads[active] ?? ads[0]} />
        <Dots count={ads.length} active={active} />
      </div>
    )}
  </>
);

/** The whole Home as C8 shows it: hero, Featured, Weekly, each with its own title and on/off. */
export const HomePreview: React.FC<{
  bg?: HeroBackground;
  featured: { enabled: boolean; title: string; ads: AdCardData[] };
  weekly: { enabled: boolean; title: string; article?: { title: string; imageKey?: string | null } | null; count: number };
  readAll: string;
  viewAll: string;
}> = ({ bg, featured, weekly, readAll, viewAll }) => (
  <>
    <FeaturedPreview ads={featured.ads} title={featured.title} readAll={readAll} bg={bg} enabled={featured.enabled} />
    {weekly.enabled && weekly.article && (
      <div style={{ padding: '0 16px', marginTop: 24 }}>
        <SectionHead title={weekly.title} more={viewAll} />
        <div style={{ width: 358 }}>
          <Img imageKey={weekly.article.imageKey} width={358} height={230} radius={8} />
          <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, marginTop: 8, ...clamp(2) }}>{weekly.article.title}</div>
        </div>
        <Dots count={weekly.count} active={0} />
      </div>
    )}
  </>
);

export interface ArticleData {
  title: string;
  content: string;
  author: string;
  imageKey?: string | null;
}

/** Home: the Weekly row, with this article as the card residents tap. */
export const ArticleHomeCard: React.FC<{ article: ArticleData; featured?: AdCardData[] }> = ({ article, featured = [] }) => (
  <>
    <FeaturedPreview ads={featured} />
    <div style={{ padding: '0 16px', marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 700 }}>Weekly</span>
        <span style={{ fontSize: 18 }}>View all</span>
      </div>
      <div style={{ width: 358 }}>
        <Img imageKey={article.imageKey} width={358} height={230} radius={8} />
        <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, marginTop: 8, ...clamp(2) }}>{article.title}</div>
      </div>
      <Dots count={4} active={0} />
    </div>
  </>
);

/** The article screen: hero, title, author, body. */
export const ArticleScreen: React.FC<{ article: ArticleData }> = ({ article }) => (
  <div>
    <div style={{ height: 48, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 16, borderBottom: '1px solid #eee' }}>
      <span aria-hidden style={{ fontSize: 18 }}>✕</span>
      <span style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 600, marginRight: 34, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{article.title}</span>
    </div>
    <Img imageKey={article.imageKey} width={390} height={250} />
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{article.title}</div>
      {article.author && <div style={{ fontSize: 14, color: '#6B7280', marginTop: 12 }}>{article.author}</div>}
      <div style={{ height: 1, background: '#E5E5E5', margin: '12px 0' }} />
      <div style={{ fontSize: 16, lineHeight: 1.5, textAlign: 'justify', color: '#111' }}>{renderMarkdownLite(article.content || '')}</div>
    </div>
  </div>
);
